import React, { useEffect, useState, useCallback } from "react";
import styled from "styled-components";
import { useAIExtraction } from "../../../hooks/useAIExtraction";
import { AIModalState } from "../../../enums/CompassAI";
import { HeroKind } from "../../../enums/Modal";
import { MESSAGES } from "../../../messages";
import { AIExtractionResponse } from "../../../types/compassAITypes";

import {
  CompassModal,
  ModalContent,
  ModalFooter,
  PreserveSpace,
  useCompassModal,
  defineModalConfig,
} from "../Modal";
import { CompassParagraph } from "../styledComponents";
import { ExtractionResultsSummary } from "./ExtractionResultsSummary";
import {
  CompassGreenButton,
  CompassWhiteButton,
} from "../../buttons/styledComponents";
import { FileDropZone } from "../FileUpload";
import { JobProgressBar } from "./JobProgressBar";

import { darkGreen, errorRed } from "../../../styles";

import aiIcon from "../../../assets/svg/compass_ai_modal_icon.svg";
import engagementExtractionBg from "../../../assets/images/engagement_record_generation.png";
import aiJobRunningAnimation from "../../../assets/svg/ai_job_running_animation.svg";

const DownloadButtonsContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  width: 100%;
`;

const messages = MESSAGES.COMPASS_AI.PDF_EXTRACTION;

type ModalContext = { dotCounter: number };

const PDF_EXTRACTION_MODAL_CONFIG = defineModalConfig<
  AIModalState,
  ModalContext
>({
  title: messages.MODAL_TITLE,
  titleIcon: aiIcon,
  titleIconSize: 40,
  modalSize: "md",
  size: "lg",
  states: {
    [AIModalState.IDLE]: {
      hero: {
        kind: HeroKind.IMAGE,
        source: engagementExtractionBg,
        padding: 0,
        imageBorderRadius: 8,
        backgroundColor: "#fafafa",
        title: messages.TITLE.IDLE,
      },
    },
    [AIModalState.UPLOADING]: {
      isProcessing: true,
      hero: {
        kind: HeroKind.IMAGE,
        source: engagementExtractionBg,
        padding: 0,
        imageBorderRadius: 8,
        backgroundColor: "#fafafa",
        title: messages.TITLE.UPLOADING,
        suffix: (ctx) =>
          [1, 2, 3].map((i) => (
            <CompassParagraph
              key={i}
              color={ctx.dotCounter >= i ? darkGreen : "transparent"}
              mr={-2}
              fontSize={20}
              bold
            >
              .
            </CompassParagraph>
          )),
      },
    },
    [AIModalState.UPLOADED]: {
      hero: {
        kind: HeroKind.IMAGE,
        source: engagementExtractionBg,
        padding: 0,
        imageBorderRadius: 8,
        backgroundColor: "#fafafa",
        title: messages.TITLE.UPLOADED,
      },
    },
    [AIModalState.EXTRACTING]: {
      isProcessing: true,
      hero: {
        kind: HeroKind.IMAGE,
        source: aiJobRunningAnimation,
        padding: 0,
        height: 280,
        backgroundColor: "#fafafa",
        title: messages.TITLE.EXTRACTING,
        description: messages.DESCRIPTION.EXTRACTING,
        suffix: (ctx) =>
          [1, 2, 3].map((i) => (
            <CompassParagraph
              key={i}
              color={ctx.dotCounter >= i ? darkGreen : "transparent"}
              mr={-2}
              fontSize={20}
              bold
            >
              .
            </CompassParagraph>
          )),
      },
    },
    [AIModalState.POLLING]: {
      isProcessing: true,
      hero: {
        kind: HeroKind.IMAGE,
        source: aiJobRunningAnimation,
        padding: 0,
        height: 280,
        backgroundColor: "#fafafa",
        title: messages.TITLE.POLLING,
        description: messages.DESCRIPTION.POLLING,
      },
    },
    [AIModalState.SUCCESS]: {},
    [AIModalState.ERROR]: {
      hero: {
        kind: HeroKind.IMAGE,
        source: engagementExtractionBg,
        padding: 0,
        imageBorderRadius: 8,
        backgroundColor: "#fafafa",
        title: messages.TITLE.ERROR,
        titleColor: errorRed,
        accentColor: errorRed,
        description: messages.DESCRIPTION.ERROR,
      },
    },
  },
});

export interface PDFExtractionModalProps {
  show: boolean;
  onHide: () => void;
  onSuccess?: (result: AIExtractionResponse) => void;
}

export const PDFExtractionModal: React.FC<PDFExtractionModalProps> = ({
  show,
  onHide,
  onSuccess,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dotCounter, setDotCounter] = useState(0);

  const {
    state,
    uploadPDF,
    startExtraction,
    downloadFile,
    extractionResult,
    progress,
    reset,
    isUploading,
  } = useAIExtraction();

  useEffect(() => {
    if (isUploading || state === AIModalState.EXTRACTING) {
      const interval = setInterval(() => {
        setDotCounter((prev) => (prev + 1) % 4);
      }, 500);
      return () => clearInterval(interval);
    }
  }, [isUploading, state]);

  const handleExtract = async (): Promise<void> => {
    await startExtraction();
  };

  useEffect(() => {
    if (state === AIModalState.SUCCESS && extractionResult && onSuccess) {
      onSuccess(extractionResult);
    }
  }, [state, extractionResult]);

  const handleClose = useCallback(() => {
    reset();
    setSelectedFile(null);
    setDotCounter(0);
    onHide();
  }, [reset, onHide]);

  const handleFileSelect = useCallback(
    async (file: File) => {
      setSelectedFile(file);
      await uploadPDF(file);
    },
    [uploadPDF],
  );

  const handleDownloadEngagements = useCallback(async () => {
    if (extractionResult?.output_engagements_csv) {
      const url = await downloadFile(extractionResult.output_engagements_csv);
      if (url) window.open(url, "_blank");
    }
  }, [extractionResult, downloadFile]);

  const handleDownloadUpdates = useCallback(async () => {
    if (extractionResult?.output_updates_csv) {
      const url = await downloadFile(extractionResult.output_updates_csv);
      if (url) window.open(url, "_blank");
    }
  }, [extractionResult, downloadFile]);

  const { modalProps, isProcessing } = useCompassModal({
    config: PDF_EXTRACTION_MODAL_CONFIG,
    state,
    context: { dotCounter },
  });

  const renderFooter = (): React.ReactNode => {
    if (state === AIModalState.SUCCESS) {
      return (
        <DownloadButtonsContainer>
          <CompassGreenButton onClick={handleDownloadEngagements}>
            {messages.DOWNLOAD_ENGAGEMENTS}
          </CompassGreenButton>
          <CompassWhiteButton onClick={handleDownloadUpdates}>
            {messages.DOWNLOAD_UPDATES}
          </CompassWhiteButton>
        </DownloadButtonsContainer>
      );
    }

    if (state === AIModalState.UPLOADED) {
      return (
        <ModalFooter
          onClose={handleClose}
          closeText="Cancel"
          submitText={messages.EXTRACT_BUTTON}
          onSubmit={handleExtract}
          showDivider={false}
        />
      );
    }

    if (state === AIModalState.ERROR) {
      return (
        <ModalFooter
          onClose={handleClose}
          closeText="Close"
          submitText="Try Again"
          onSubmit={reset}
          showDivider={false}
        />
      );
    }

    return (
      <ModalFooter
        onClose={handleClose}
        closeText="Cancel"
        hideSubmit
        isLoading={isProcessing}
        showDivider={false}
      />
    );
  };

  return (
    <CompassModal
      show={show}
      onHide={handleClose}
      {...modalProps}
      footer={renderFooter()}
    >
      <ModalContent>
        <PreserveSpace
          hidden={
            state === AIModalState.EXTRACTING ||
            state === AIModalState.POLLING ||
            state === AIModalState.SUCCESS
          }
        >
          <FileDropZone
            file={selectedFile}
            isUploading={isUploading}
            onFileSelect={handleFileSelect}
            fileTypesDescription={messages.FILE_TYPES}
            fileTypeLabel="PDF"
            accept=".pdf,application/pdf"
            showFileInfo={
              state === AIModalState.UPLOADING ||
              state === AIModalState.UPLOADED
            }
          />
        </PreserveSpace>

        {state === AIModalState.POLLING && progress && (
          <JobProgressBar progress={progress} />
        )}

        {state === AIModalState.SUCCESS && extractionResult && (
          <ExtractionResultsSummary result={extractionResult} />
        )}
      </ModalContent>
    </CompassModal>
  );
};
